import { computed, unref, type ComputedRef, type Ref } from 'vue';

type MaybeRef<T> = T | Ref<T> | ComputedRef<T>;

const adjustColorBrightness = (hex: string, factor: number) => {
  const c_r = parseInt(hex.substring(0, 2), 16);
  const c_g = parseInt(hex.substring(2, 4), 16);
  const c_b = parseInt(hex.substring(4, 6), 16);

  const adjust = (color: number) =>
    Math.max(0, Math.min(255, Math.floor(color * factor)));

  const new_r = adjust(c_r);
  const new_g = adjust(c_g);
  const new_b = adjust(c_b);

  return `#${new_r.toString(16).padStart(2, '0')}${new_g
    .toString(16)
    .padStart(2, '0')}${new_b.toString(16).padStart(2, '0')}`;
};

const computeIntegrationStyles = (primaryColor?: string) => {
  const hex = primaryColor?.replace('#', '') || '';
  const c_r = parseInt(hex.substring(0, 2), 16);
  const c_g = parseInt(hex.substring(2, 4), 16);
  const c_b = parseInt(hex.substring(4, 6), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
  const brandColor = brightness > 155 ? '#1C1F27' : '#FFFFFF';
  return brandColor;
};

const computeReverseIntegrationStyles = (brandBg: string) => {
  const hex = brandBg.replace('#', '');
  const c_r = parseInt(hex.substring(0, 2), 16);
  const c_g = parseInt(hex.substring(2, 4), 16);
  const c_b = parseInt(hex.substring(4, 6), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
  const brandColor = brightness > 155 ? '#1C1F27' : brandBg;
  return brandColor;
};

const computeIntegrationBrandColor = (primaryColor?: string) => {
  const hex = primaryColor?.replace('#', '') || '';
  const c_r = parseInt(hex.substring(0, 2), 16);
  const c_g = parseInt(hex.substring(2, 4), 16);
  const c_b = parseInt(hex.substring(4, 6), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;

  const factor = brightness > 155 ? 0.8 : 1;
  return brightness > 155 ? adjustColorBrightness(hex, factor) : `#${hex}`;
};

export function useHelpeeBranding(params: {
  primaryColor?: MaybeRef<string | undefined>;
  headerUrl?: MaybeRef<string | undefined>;
}) {
  const primaryColor = computed(() => unref(params.primaryColor));
  const headerUrl = computed(() => unref(params.headerUrl));

  const headerBackgroundUrl = computed(() => {
    return headerUrl.value
      ? `url(${headerUrl.value})`
      : 'url(\'/img/illustration_helpee_signup_lg.png\')';
  });

  const brandTextColor = computed(() =>
    computeIntegrationStyles(primaryColor.value)
  );
  const brandLinkColor = computed(() =>
    computeReverseIntegrationStyles(primaryColor.value || '#1C1F27')
  );
  const brandColor = computed(() =>
    computeIntegrationBrandColor(primaryColor.value)
  );

  return {
    headerBackgroundUrl,
    brandTextColor,
    brandLinkColor,
    brandColor,
  };
}
